import { periodCountFromSegment } from '../../helpers/matchTiming';
import { EventLightVm } from '../../utils/api';
import {
    buildMatchNarrative,
    buildMatchPeriodDetails,
    buildMatchSituationPanel,
    MatchNarrative,
    MatchPeriodDetail,
    MatchSituationPanel,
} from './insightsMatchAnalysis';
import {
    eventClock,
    formatPeriodLabel,
    formatShotTypeLabel,
    nameByMatchPlayerId,
    sortEventsChronological,
} from './insightsMatchStats';
import { MatchBundle } from './insightsQueries';
import { conversionPercent } from './insightsTeamSplits';

/*
 * Momentum + richer match report from light events (heuristic, not possession model).
 */

export type MomentumKind =
    | 'start'
    | 'goalFor'
    | 'goalAgainst'
    | 'save'
    | 'exclusionDrawn'
    | 'exclusionCommitted'
    | 'turnover';

export interface MomentumPoint {
    index: number;
    /** Our cumulative momentum (can go negative). */
    value: number;
    delta: number;
    label: string;
    eventId: number;
    kind: MomentumKind;
    headline: string;
    playerLine?: string;
    typeLine?: string;
    usScore: number;
    themScore: number;
}

const MOMENTUM_HEADLINE: Record<MomentumKind, string> = {
    start: 'Kickoff',
    goalFor: 'Goal for us',
    goalAgainst: 'Goal against',
    save: 'Save',
    exclusionDrawn: 'Exclusion drawn',
    exclusionCommitted: 'Exclusion committed',
    turnover: 'Turnover',
};

const nameOf = (names: Map<number, string>, matchPlayerId?: number): string | undefined => {
    if (matchPlayerId == null) return undefined;
    return names.get(matchPlayerId);
};

const joinNames = (...parts: (string | undefined)[]): string | undefined => {
    const text = parts.filter(Boolean).join(' · ');
    return text || undefined;
};

/**
 * Running "control" score for our club:
 * +3 goal for, -3 goal against, +1 exclusion drawn (opp excluded), -1 we excluded,
 * +1 save, -1 turnover for us.
 */
export const buildMomentumSeries = (bundle: MatchBundle, ourTeamIds: number[]): MomentumPoint[] => {
    const names = nameByMatchPlayerId(bundle.detail);
    const periodCount = periodCountFromSegment(bundle.detail.matchSegmentId || 0);
    const points: MomentumPoint[] = [
        {
            index: 0,
            value: 0,
            delta: 0,
            label: 'Start',
            eventId: 0,
            kind: 'start',
            headline: MOMENTUM_HEADLINE.start,
            usScore: 0,
            themScore: 0,
        },
    ];
    let value = 0;
    let usScore = 0;
    let themScore = 0;
    let i = 0;

    sortEventsChronological(bundle.events).forEach((event: EventLightVm) => {
        let delta = 0;
        let kind: MomentumKind | undefined;
        let playerLine: string | undefined;
        let typeLine: string | undefined;

        if (event.shot) {
            const ours = ourTeamIds.includes(event.shot.teamId);
            if (event.shot.isGoal) {
                kind = ours ? 'goalFor' : 'goalAgainst';
                delta = ours ? 3 : -3;
                if (ours) usScore += 1;
                else themScore += 1;
                playerLine = joinNames(
                    nameOf(names, event.shot.takenById),
                    event.shot.assistedById != null
                        ? `A ${nameOf(names, event.shot.assistedById)}`
                        : undefined
                );
                typeLine = formatShotTypeLabel(event.shot.type, event.shot.isFastBreak);
            } else if (!ours && event.shot.savedById != null) {
                kind = 'save';
                delta = 1;
                playerLine = nameOf(names, event.shot.savedById);
                typeLine = formatShotTypeLabel(event.shot.type, event.shot.isFastBreak);
            } else {
                return;
            }
        } else if (event.exclusion) {
            const weFouled = ourTeamIds.includes(event.exclusion.teamId);
            kind = weFouled ? 'exclusionCommitted' : 'exclusionDrawn';
            delta = weFouled ? -1 : 1;
            playerLine = joinNames(
                nameOf(names, event.exclusion.excludedPlayerId),
                event.exclusion.fouledPlayerId != null
                    ? `drawn by ${nameOf(names, event.exclusion.fouledPlayerId)}`
                    : undefined
            );
        } else if (event.turnover && ourTeamIds.includes(event.turnover.teamId)) {
            kind = 'turnover';
            delta = -1;
            playerLine = joinNames(
                nameOf(names, event.turnover.lostPossesionPlayerId),
                event.turnover.wonPossesionPlayerId != null
                    ? `to ${nameOf(names, event.turnover.wonPossesionPlayerId)}`
                    : undefined
            );
        } else {
            return;
        }

        if (!kind) return;

        value += delta;
        i += 1;
        const period = formatPeriodLabel(event.period, event.periodId, periodCount);
        points.push({
            index: i,
            value,
            delta,
            label: `${period} ${eventClock(event)}`.trim(),
            eventId: event.id,
            kind,
            headline: MOMENTUM_HEADLINE[kind],
            playerLine,
            typeLine,
            usScore,
            themScore,
        });
    });

    return points;
};

export interface MatchReportSection {
    title: string;
    lines: string[];
}

export interface MatchReport {
    narrative: MatchNarrative;
    keyNumbers: { label: string; value: string }[];
    sections: MatchReportSection[];
}

export const buildMatchReport = (
    bundle: MatchBundle,
    ourTeamIds: number[],
    ourGoals: number,
    theirGoals: number
): MatchReport => {
    const panel: MatchSituationPanel = buildMatchSituationPanel(bundle, ourTeamIds);
    const periods: MatchPeriodDetail[] = buildMatchPeriodDetails(bundle, ourTeamIds);
    const narrative = buildMatchNarrative(panel, periods, ourGoals, theirGoals);

    const resultLabel = ourGoals > theirGoals ? 'Win' : ourGoals < theirGoals ? 'Loss' : 'Draw';

    const keyNumbers = [
        { label: 'Result', value: `${resultLabel} ${ourGoals}:${theirGoals}` },
        {
            label: 'Shooting',
            value: (() => {
                const shots = panel.rows.reduce((s, r) => s + r.us.shots, 0);
                const goals = panel.rows.reduce((s, r) => s + r.us.goals, 0);
                return shots > 0
                    ? `${Math.round((goals / shots) * 100)}% (${goals}/${shots})`
                    : '-';
            })(),
        },
        {
            label: 'Penalty kill',
            value: panel.penaltyKillPct == null ? '-' : `${panel.penaltyKillPct}%`,
        },
        {
            label: 'EP conversion',
            value: (() => {
                const ep = panel.rows.find((r) => r.type === 'extraPlayer');
                const pct = ep ? conversionPercent(ep.us) : undefined;
                return pct == null || !ep ? '-' : `${pct}% (${ep.us.goals}/${ep.us.shots})`;
            })(),
        },
    ];

    const attackLines = panel.rows
        .filter((r) => r.us.shots > 0 || r.them.shots > 0)
        .map((r) => {
            const us = r.usPct == null ? `${r.us.goals}/${r.us.shots}` : `${r.usPct}%`;
            const them = r.themPct == null ? `${r.them.goals}/${r.them.shots}` : `${r.themPct}%`;
            return `${r.label}: us ${us} · opp ${them}`;
        });

    const periodLines = periods.map((p) => {
        const diff = p.goalsFor - p.goalsAgainst;
        const sign = diff > 0 ? '+' : '';
        return `${p.label}: ${p.goalsFor}:${p.goalsAgainst} (${sign}${diff}) · Sh ${p.shotsFor}/${p.shotsAgainst} · Ex ${p.exclusionsFor}/${p.exclusionsAgainst} · TO ${p.turnoversFor}/${p.turnoversAgainst}`;
    });

    const softPeriod = [...periods].sort(
        (a, b) => a.goalsFor - a.goalsAgainst - (b.goalsFor - b.goalsAgainst)
    )[0];
    const strongPeriod = [...periods].sort(
        (a, b) => b.goalsFor - b.goalsAgainst - (a.goalsFor - a.goalsAgainst)
    )[0];

    const sections: MatchReportSection[] = [
        { title: 'Attack mix', lines: attackLines.length > 0 ? attackLines : ['No shots logged'] },
        { title: 'By period', lines: periodLines.length > 0 ? periodLines : ['No period events'] },
        {
            title: 'Takeaways',
            lines: [
                narrative.well,
                narrative.poorly,
                narrative.turningPoint,
                strongPeriod
                    ? `Best period ${strongPeriod.label} (${strongPeriod.goalsFor}:${strongPeriod.goalsAgainst})`
                    : '',
                softPeriod && softPeriod !== strongPeriod
                    ? `Softest period ${softPeriod.label} (${softPeriod.goalsFor}:${softPeriod.goalsAgainst})`
                    : '',
            ].filter(Boolean),
        },
    ];

    return { narrative, keyNumbers, sections };
};
